<template>
  <div>
    <div class="row">
      <div class="form-group col-md-4">
        <label class="mb-1"><b><u class="section-title"><i class="fas fa-chart-pie"></i>&nbsp;&nbsp;Resumen de Contenido&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u></b></label>
        <v-select v-model="selectedCourse" :options="courses" class="my-custom-select" placeholder="Todos los cursos"></v-select>
      </div>
      <div class="col-md-12">
        <button class="btn btn-sm btn-danger btn-icon-split" title="Ver todos los cursos" @click="clearFilter()"><i class="fas fa-ban"></i></button>
        <hr>
      </div>
    </div>

    <b-overlay :show="loading" rounded="sm">

      <!-- Tarjetas KPI -->
      <div class="kpi-grid">
        <div class="kpi-card" v-for="kpi in kpiCards" :key="kpi.key">
          <div class="kpi-icon" :style="{ backgroundColor: kpi.color }"><i :class="kpi.icon"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.value }}</div>
            <div class="kpi-label">{{ kpi.label }}</div>
          </div>
        </div>
      </div>

      <!-- Preparación para evaluación -->
      <div class="row mt-2">
        <div class="col-lg-5">
          <div class="panel-block">
            <div class="panel-title">Preparación para evaluación</div>
            <div ref="readinessChart"></div>
            <p class="text-muted small mb-2">
              Un módulo necesita al menos <b>{{ readiness.min_questions }}</b> preguntas para generar examen.
            </p>
            <div class="quality-row">
              <span :class="readiness.questions_without_answers ? 'text-danger' : 'text-muted'">
                <i class="fas" :class="readiness.questions_without_answers ? 'fa-exclamation-triangle' : 'fa-check-circle'"></i>
                {{ readiness.questions_without_answers }} preguntas sin respuestas
              </span>
              <span :class="readiness.questions_without_correct ? 'text-danger' : 'text-muted'">
                <i class="fas" :class="readiness.questions_without_correct ? 'fa-exclamation-triangle' : 'fa-check-circle'"></i>
                {{ readiness.questions_without_correct }} sin acierto marcado
              </span>
            </div>
            <a :href="exportUrl" class="btn btn-sm btn-secondary mb-2" title="Descargar en Excel las preguntas incompletas">
              <i class="fas fa-file-excel"></i> Exportar preguntas incompletas
            </a>
            <div v-if="readiness.modules_not_ready && readiness.modules_not_ready.length" class="not-ready-list">
              <div class="not-ready-title">Módulos que aún no generan examen</div>
              <div v-for="(m, i) in readiness.modules_not_ready" :key="i" class="not-ready-item">
                <span class="not-ready-name">{{ m.module }}</span>
                <span class="badge badge-danger">{{ m.questions }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Contenido por módulo -->
        <div class="col-lg-7">
          <div class="panel-block">
            <div class="panel-title">Contenido por módulo</div>
            <div v-show="byModule.length" ref="moduleChart"></div>
            <p v-if="!byModule.length && !loading" class="text-muted mb-0">Este curso no tiene módulos asociados.</p>
          </div>
        </div>
      </div>

      <!-- Distribución de preguntas -->
      <div class="row mt-2">
        <div class="col-md-6">
          <div class="panel-block">
            <div class="panel-title">Preguntas por nivel</div>
            <div v-show="hasQuestions" ref="levelChart"></div>
            <p v-if="!hasQuestions && !loading" class="text-muted mb-0">Todavía no hay preguntas cargadas.</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="panel-block">
            <div class="panel-title">Preguntas por tipo de respuesta</div>
            <div v-show="hasQuestions" ref="typeChart"></div>
            <p v-if="!hasQuestions && !loading" class="text-muted mb-0">Todavía no hay preguntas cargadas.</p>
          </div>
        </div>
      </div>

      <!-- ============ Alumnos ============ -->
      <div class="section-divider">
        <span><i class="fas fa-user-graduate"></i>&nbsp;&nbsp;Alumnos</span>
      </div>

      <div class="row">
        <div class="col-lg-5">
          <div class="panel-block">
            <div class="panel-title">Estado de las matrículas</div>
            <div ref="vigencyChart"></div>
            <div v-if="vigency.expired_list && vigency.expired_list.length" class="not-ready-list">
              <div class="not-ready-title">Matrículas vencidas</div>
              <div v-for="(e, i) in vigency.expired_list" :key="i" class="not-ready-item">
                <span class="not-ready-name">{{ e.student }} — {{ e.course }}</span>
                <span class="badge badge-danger">{{ e.days }}d</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-7">
          <div class="panel-block">
            <div class="panel-title">Embudo de avance</div>
            <div ref="funnelChart"></div>
          </div>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col-12">
          <div class="panel-block">
            <div class="panel-title">Ingresos de los últimos 30 días</div>
            <div ref="loginsChart"></div>
          </div>
        </div>
      </div>

    </b-overlay>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { BOverlay } from 'bootstrap-vue'

// Paleta del tema (public/js/app-assets/js/scripts/charts/apexcharts/charts-apexcharts.js)
const COLORS = {
  primary:   '#00b5b8',
  success:   '#16d39a',
  info:      '#2dcee3',
  warning:   '#ffa87d',
  danger:    '#ff7588',
  secondary: '#404e67',
}

export default {
  components: { vSelect, BOverlay },
  data() {
    return {
      loading: false,
      courses: [],
      selectedCourse: null,
      kpis: {},
      byModule: [],
      readiness: { modules_not_ready: [], min_questions: 5, questions_without_answers: 0, questions_without_correct: 0 },
      byLevel: [],
      byTypeanswer: [],
      vigency: { expired_list: [] },
      funnel: {},
      logins: [],
      charts: {},
    }
  },
  computed: {
    hasQuestions() {
      return (this.kpis.questions || 0) > 0
    },
    exportUrl() {
      const base = '/administration/courses/questions/export'
      return this.selectedCourse ? base + '?course_id=' + this.selectedCourse.value : base
    },
    kpiCards() {
      return [
        { key: 'modules',     label: 'Módulos',     value: this.kpis.modules     || 0, icon: 'feather icon-layers',    color: COLORS.primary },
        { key: 'topics',      label: 'Temas',       value: this.kpis.topics      || 0, icon: 'fas fa-align-justify',   color: COLORS.info },
        { key: 'definitions', label: 'Definiciones',value: this.kpis.definitions || 0, icon: 'fas fa-align-left',      color: COLORS.success },
        { key: 'questions',   label: 'Preguntas',   value: this.kpis.questions   || 0, icon: 'fas fa-stream',          color: COLORS.warning },
        { key: 'answers',     label: 'Respuestas',  value: this.kpis.answers     || 0, icon: 'fas fa-list-ul',         color: COLORS.danger },
        { key: 'students',    label: 'Alumnos',     value: this.kpis.students    || 0, icon: 'fas fa-user-graduate',   color: COLORS.secondary },
      ]
    },
  },
  watch: {
    selectedCourse() {
      this.getMetrics()
    },
  },
  created() {
    this.getCourses()
    this.getMetrics()
  },
  beforeDestroy() {
    this.destroyCharts()
  },
  methods: {
    /**
     * apexcharts.min.js se carga en el layout DESPUÉS de mix('js/app.js'), así que
     * en mounted() window.ApexCharts todavía no existe. Normalmente la respuesta de
     * axios llega más tarde, pero una respuesta cacheada podría adelantarse.
     */
    whenApexReady(callback) {
      if (window.ApexCharts) {
        callback()
      } else {
        window.addEventListener('load', callback, { once: true })
      }
    },
    getCourses() {
      axios.get('/dashboard/getCourses')
        .then(response => {
          this.courses = response.data.data
        })
        .catch(error => {
          console.error(error)
        })
    },
    clearFilter() {
      this.selectedCourse = null
    },
    getMetrics() {
      this.loading = true
      const params = { course_id: this.selectedCourse ? this.selectedCourse.value : null }

      Promise.all([
        axios.get('/dashboard/metrics', { params: params }),
        axios.get('/dashboard/studentMetrics', { params: params }),
      ])
        .then(([content, students]) => {
          const d = content.data.data
          this.kpis = d.kpis
          this.byModule = d.by_module
          this.readiness = d.readiness
          this.byLevel = d.by_level
          this.byTypeanswer = d.by_typeanswer

          const s = students.data.data
          this.vigency = s.vigency
          this.funnel = s.funnel
          this.logins = s.logins

          this.$nextTick(() => this.whenApexReady(this.renderCharts))
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    destroyCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) {
          this.charts[key].destroy()
          this.charts[key] = null
        }
      })
    },
    renderCharts() {
      // Sin destruir primero, ApexCharts deja gráficos huérfanos al cambiar de curso.
      this.destroyCharts()
      this.renderReadiness()
      this.renderByModule()
      this.renderDonut('levelChart', this.byLevel, [COLORS.success, COLORS.warning, COLORS.danger])
      this.renderDonut('typeChart', this.byTypeanswer, [COLORS.primary, COLORS.info])
      this.renderVigency()
      this.renderFunnel()
      this.renderLogins()
    },
    renderVigency() {
      const el = this.$refs.vigencyChart
      if (!el) return

      this.charts.vigency = new window.ApexCharts(el, {
        chart: { type: 'donut', height: 240 , animations: { enabled: false }},
        labels: ['Vigentes', 'Vencen en 7 días', 'Vencidas'],
        series: [this.vigency.vigente || 0, this.vigency.por_vencer || 0, this.vigency.vencida || 0],
        colors: [COLORS.success, COLORS.warning, COLORS.danger],
        legend: { position: 'bottom' },
        plotOptions: {
          pie: { donut: { labels: { show: true, total: { show: true, label: 'Matrículas' } } } }
        },
        noData: { text: 'Sin matrículas' },
      })
      this.charts.vigency.render()
    },
    renderFunnel() {
      const el = this.$refs.funnelChart
      if (!el) return

      // ApexCharts 3.6.12 (2019) no pinta las barras con `distributed: true`
      // en horizontal: se usa un color único, que además es lo correcto para
      // un embudo (es la misma magnitud en tres etapas).
      this.charts.funnel = new window.ApexCharts(el, {
        chart: { type: 'bar', height: 240, animations: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%' } },
        series: [{
          name: 'Matrículas',
          data: [this.funnel.enrolled || 0, this.funnel.started || 0, this.funnel.completed || 0],
        }],
        xaxis: { categories: ['Matriculados', 'Iniciaron', 'Completaron'] },
        colors: [COLORS.primary],
        legend: { show: false },
        dataLabels: { enabled: true },
        grid: { borderColor: '#e8edf3' },
      })
      this.charts.funnel.render()
    },
    renderLogins() {
      const el = this.$refs.loginsChart
      if (!el) return

      // Barras en lugar de área: con accesos concentrados en pocos días, un área
      // deja una línea plana en 0 y el pico se pierde en el borde del gráfico.
      this.charts.logins = new window.ApexCharts(el, {
        chart: { type: 'bar', height: 240, animations: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%' } },
        series: [{ name: 'Accesos', data: this.logins.map(d => d.total) }],
        xaxis: {
          categories: this.logins.map(d => d.date.substring(8) + '/' + d.date.substring(5, 7)),
          tickAmount: 10,
        },
        yaxis: { labels: { formatter: v => Math.round(v) } },
        colors: [COLORS.primary],
        dataLabels: { enabled: false },
        grid: { borderColor: '#e8edf3' },
      })
      this.charts.logins.render()
    },
    renderReadiness() {
      const el = this.$refs.readinessChart
      if (!el) return

      this.charts.readiness = new window.ApexCharts(el, {
        chart: { type: 'donut', height: 240 , animations: { enabled: false }},
        labels: ['Listos para examen', 'Sin preguntas suficientes'],
        series: [this.readiness.ready || 0, this.readiness.not_ready || 0],
        colors: [COLORS.success, COLORS.danger],
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
        plotOptions: {
          pie: { donut: { labels: { show: true, total: { show: true, label: 'Módulos' } } } }
        },
        noData: { text: 'Sin módulos' },
      })
      this.charts.readiness.render()
    },
    renderByModule() {
      const el = this.$refs.moduleChart
      if (!el || !this.byModule.length) return

      this.charts.module = new window.ApexCharts(el, {
        chart: { type: 'bar', height: Math.max(260, this.byModule.length * 42), animations: { enabled: false }, stacked: true, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '65%' } },
        series: [
          { name: 'Temas',        data: this.byModule.map(m => m.topics) },
          { name: 'Definiciones', data: this.byModule.map(m => m.definitions) },
          { name: 'Preguntas',    data: this.byModule.map(m => m.questions) },
        ],
        xaxis: { categories: this.byModule.map(m => m.module) },
        colors: [COLORS.info, COLORS.success, COLORS.warning],
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        grid: { borderColor: '#e8edf3' },
      })
      this.charts.module.render()
    },
    renderDonut(ref, dataset, colors) {
      const el = this.$refs[ref]
      if (!el || !this.hasQuestions) return

      this.charts[ref] = new window.ApexCharts(el, {
        chart: { type: 'donut', height: 240 , animations: { enabled: false }},
        labels: dataset.map(d => d.label),
        series: dataset.map(d => d.total),
        colors: colors,
        legend: { position: 'bottom' },
        plotOptions: {
          pie: { donut: { labels: { show: true, total: { show: true, label: 'Preguntas' } } } }
        },
      })
      this.charts[ref].render()
    },
  }
}
</script>
